import {type Dispatch} from 'react';
import {Stack, Tooltip, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {type IFishGroup, type IFishGroupAction} from './helpers/reducer';
import {FishGroupPropertiesTags} from 'components/Shared/FishGroupPropertiesTags';
import {arraysAreEqual} from 'utils/arraysAreEqual';
import EditedIcon from '@mui/icons-material/DriveFileRenameOutlineRounded';
import type {FishGroup} from 'client/bioplanModels/fish-group';

const disabled = true;

export const FishGroupPropertiesRow = ({
  state,
  dispatch,
}: {
  state: IFishGroup;
  dispatch: Dispatch<IFishGroupAction>;
}): JSX.Element => {
  const {t} = useTranslation('common');
  const isMixedState = checkIsMixedState(state.fishGroups);

  const isEdited = state.fishGroups.some((fishGroup) => Boolean(fishGroup.propertiesAttribute.isPinned));
  const selectedProperties = getSelectedProperties(state.fishGroups);

  return (
    <Stack direction="row" alignItems="center" minHeight={32}>
      <Stack direction="row" width={90} spacing={0.5} alignItems="center">
        <Typography variant="body2" color={'secondary'}>
          {t('properties')}
        </Typography>

        {isEdited && (
          <Tooltip title={t('isEdited')}>
            <EditedIcon fontSize={'inherit'} color="primary" />
          </Tooltip>
        )}
      </Stack>

      <FishGroupPropertiesTags
        selectedTags={selectedProperties}
        onSelectionChange={() => {}}
        onAfterCreate={() => {}}
        translationKeys={{addNewLabel: isMixedState ? t('mixed') : t('addFishGroupProperty')}}
        disabled={disabled}
      />
    </Stack>
  );
};

export function checkIsMixedState(fishGroups: FishGroup[]) {
  return fishGroups.some((fishGroup) => {
    const otherFishGroups = fishGroups.filter((otherFishGroup) => otherFishGroup.id !== fishGroup.id);

    if (otherFishGroups.length === 0) {
      return false;
    }

    return otherFishGroups.some((otherFishGroup) => {
      const properties = fishGroup.propertiesAttribute.value?.map((value) => value.id);
      const otherProperties = otherFishGroup.propertiesAttribute.value?.map((value) => value.id);

      if (Array.isArray(properties) && Array.isArray(otherProperties)) {
        return !arraysAreEqual(properties, otherProperties);
      }

      return properties !== otherProperties;
    });
  });
}

export function getSelectedProperties(fishGroups: FishGroup[]) {
  const isMixedState = checkIsMixedState(fishGroups);

  return isMixedState
    ? []
    : Array.from(
        new Set(
          fishGroups
            .flatMap((fishGroup) => fishGroup.propertiesAttribute.value?.map((value) => value.id))
            .filter((id): id is string => Boolean(id)),
        ),
      );
}
